import { useMemo } from 'react'
import { Box, Stack } from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import { DataPieChart, PieChartDataItem } from '../components/DataPieChart'
import { ChartCard } from '../components/DataChart'
import { HistoricalRequestsTable } from './HistoricalRequestsTable'
import { HistoricalJurisdictionTable } from './HistoricalJurisdictionTable'
import { RightsFilters } from 'pages/insights/rightsReports/components/RightsFilters'
import { useFetchRequestsPerJurisdiction } from './mutations/useFetchRequestsPerJurisdiction'
import { useFetchRequestsPerProperty } from './mutations/useFetchRequestsPerProperty'
import { useFetchRequestsPerRight } from './mutations/useFetchRequestsPerRight'
import { showToast } from 'components/modals/AlertComponent'
import { useFetchHistoricalRightsExport } from './mutations/useFetchHistoricalRightsExport'
import { CSVLink } from 'react-csv'
import DateRangePicker from '../components/DateRangePicker'
import { useFiltersV3 } from 'utils/hooks'
import dayjs from 'dayjs'
import { useQueryParams } from 'utils/hooks'
import { useAuth } from 'utils/hooks/useAuth'

interface PolicyScope {
  policyScopeCode: string
  policyScopeName?: string
  complete: number
  rejected?: number
  fulfilledOnTime: number
  meanFulfillmentTime?: number
  rights?: {
    rightCode: string
    rightName: string
    total: number
  }[]
}

interface Application {
  applicationCode: string
  applicationName?: string
  total: number
  rights?: {
    rightCode: string
    rightName: string
    total: number
  }[]
}

export interface GenericPieChartProps {
  data: PieChartDataItem[]
  isLoading: boolean
}

export const GenericPieChart: React.FC<GenericPieChartProps> = ({ data, isLoading }) => {
  const chartData = isLoading || !data || data.length === 0 ? [] : data
  return <DataPieChart data={chartData} />
}

type RightsQueryParams = {
  policyScope?: string
  dataSubjectType?: string
  dataRole?: string
  rightCode?: string
  applicationCode?: string
}

const reportTypeLabelMap = {
  right: 'by-right-name',
  'canonical-right': 'by-right-type',
} as const

export const HistoricalTab = () => {
  const { userData } = useAuth()

  const [propertyData, setPropertyData] = useState<Application[]>([])
  const [jurisdictionData, setJurisdictionData] = useState<PolicyScope[]>([])
  const [rightNameData, setRightNameData] = useState<any[]>([])
  const [rightTypeData, setRightTypeData] = useState<any[]>([])
  const [reportType, setReportType] = useState<'right' | 'canonical-right'>('right')
  const [csvData, setCsvData] = useState<string[][]>([])
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)
  const [shouldDownload, setShouldDownload] = useState(false)

  const { getParamValueByKey } = useFiltersV3()
  const invokedAtStart = getParamValueByKey('invokedAtStart')
  const invokedAtEnd = getParamValueByKey('invokedAtEnd')
  const { queries } = useQueryParams<RightsQueryParams>()
  const { policyScope, dataSubjectType, dataRole, rightCode, applicationCode } = queries

  const orgCode = userData.organizationCode
  const startDate = invokedAtStart?.trim() || dayjs().subtract(5, 'year').format('YYYY-MM-DD')
  const endDate = invokedAtEnd?.trim() || dayjs().format('YYYY-MM-DD')

  const { mutateAsync: fetchRequestsPerJurisdiction, isLoading: isJurisdictionLoading } =
    useFetchRequestsPerJurisdiction({
      onSuccess: data => {
        setJurisdictionData(data.data?.rightInvocationStatsPerPolicyScope ?? [])
      },
      onError: () => {
        showToast({ content: 'Failed to fetch jurisdiction stats', type: 'error' })
      },
    })

  const { mutateAsync: fetchRequestsPerProperty, isLoading: isRequestsPerPropertyLoading } =
    useFetchRequestsPerProperty({
      onSuccess: data => {
        setPropertyData(data.data?.rightInvocationStatsPerApplication ?? [])
      },
      onError: () => {
        showToast({ content: 'Failed to fetch property stats', type: 'error' })
      },
    })

  const { mutateAsync: fetchRequestsPerRight, isLoading: isRequestsPerRightLoading } = useFetchRequestsPerRight({
    onSuccess: data => {
      const apiData = data.data?.rightInvocationStatsPerRight ?? []
      if (reportType === 'right') {
        setRightNameData(apiData)
      } else {
        setRightTypeData(apiData)
      }
    },
    onError: () => {
      showToast({ content: 'Failed to fetch right stats', type: 'error' })
    },
  })

  const { mutateAsync: exportRights } = useFetchHistoricalRightsExport({
    onSuccess: response => {
      const csvString = response.data.historicalRightInvocationStatsPerRightExportData
      if (csvString) {
        const csvArray = csvString.split('\n').map(row => row.split(','))
        setCsvData(csvArray)
        showToast({ content: 'Export ready for download', type: 'success' })
      } else {
        showToast({ content: 'No CSV data returned', type: 'warning' })
      }
    },
    onError: () => {
      showToast({ content: 'Failed to download export', type: 'error' })
    },
  })

  const apiParams = useMemo(
    () => ({
      orgCode,
      startDate,
      endDate,
      ...(policyScope && { policyScope }),
      ...(dataSubjectType && { dataSubjectType }),
      ...(dataRole && { dataRole }),
      ...(rightCode && { rightCode }),
      ...(applicationCode && { applicationCode }),
    }),
    [orgCode, startDate, endDate, policyScope, dataSubjectType, dataRole, rightCode, applicationCode],
  )

  const rightsApiParams = useMemo(
    () => ({
      ...apiParams,
      reportType,
    }),
    [apiParams, reportType],
  )

  useEffect(() => {
    fetchRequestsPerJurisdiction({ params: apiParams })
    fetchRequestsPerProperty({ params: apiParams })
  }, [apiParams, fetchRequestsPerJurisdiction, fetchRequestsPerProperty])

  useEffect(() => {
    fetchRequestsPerRight({ params: rightsApiParams })
  }, [rightsApiParams, fetchRequestsPerRight])

  useEffect(() => {
    if (shouldDownload && csvData.length && csvLinkRef.current) {
      csvLinkRef.current.link.click()
      setShouldDownload(false)
    }
  }, [shouldDownload, csvData])

  const handleExportClick = async () => {
    try {
      const response = await exportRights({ params: { orgCode, startDate, endDate, reportType } })
      const csvString = response.data?.historicalRightInvocationStatsPerRightExportData
      if (csvString) {
        const csvArray = csvString.split('\n').map(row => row.split(','))
        setCsvData(csvArray)
        setShouldDownload(true)
        showToast({ content: 'Export is downloading', type: 'success' })
      } else {
        showToast({ content: 'No CSV data returned', type: 'warning' })
      }
    } catch (error) {
      showToast({ content: 'Failed to download export', type: 'error' })
    }
  }

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={3} pb={4}>
      <Stack direction="row" justifyContent="space-between">
        <RightsFilters />
        <DateRangePicker />
      </Stack>
      <Box sx={{ width: '100%', maxWidth: '1280px', overflowX: 'hidden', mx: 'auto' }}>
        <HistoricalRequestsTable
          reportType={reportType}
          setReportType={setReportType}
          rightNameData={rightNameData}
          rightTypeData={rightTypeData}
          isLoading={isRequestsPerRightLoading}
          onExportClick={handleExportClick}
        />
        <CSVLink
          data={csvData}
          filename={`historical-rights-export-${reportTypeLabelMap[reportType]}.csv`}
          className="hidden"
          ref={csvLinkRef}
          target="_blank"
        />
      </Box>
      <Box display="flex" gap={3}>
        <Box sx={{ flex: 0.7, maxWidth: '1280px', overflowX: 'hidden', mx: 'auto' }}>
          <HistoricalJurisdictionTable data={jurisdictionData} isLoading={isJurisdictionLoading} />
        </Box>
        <Stack spacing={3} sx={{ flex: 0.3 }}>
          <ChartCard title="Request Volume By Property" isLoading={isRequestsPerPropertyLoading}>
            <GenericPieChart
              data={propertyData.map(app => ({
                name: app.applicationName ?? app.applicationCode,
                value: app.total,
                originalCount: app.total,
                rights: app.rights,
              }))}
              isLoading={isRequestsPerPropertyLoading}
            />
          </ChartCard>
          <ChartCard title="Request Volume By Jurisdictions" isLoading={isJurisdictionLoading}>
            <GenericPieChart
              data={jurisdictionData.map(scope => {
                const total = (scope.complete ?? 0) + (scope.rejected ?? 0)
                return {
                  name: scope.policyScopeName ?? scope.policyScopeCode,
                  value: total,
                  originalCount: total,
                  rights: scope.rights,
                }
              })}
              isLoading={isJurisdictionLoading}
            />
          </ChartCard>
        </Stack>
      </Box>
    </Box>
  )
}
